//      
import React, { Component } from 'react';
import { Formik, Form } from 'formik';
import { Link } from 'react-router-dom';
import { omit } from 'lodash';

import { options, post } from 'api';
import { errorResponseFormatter } from 'utils/apiHelpers';
import { chainValidators, isRequired, isEmail, isPhoneNumber } from 'utils/formHelpers';
import { maxBirthDate, minBirthDate } from 'utils/dates';
import Fetcher from 'components/Fetcher';
import { Alert } from 'bs';
import {
    Input,
    FormikDatePicker,
    FormikPhoneNumber,
    FormikSelectInput,
} from 'components/FormikFields';
import { mapJurisdictionToPhoneCountry } from 'bs/PhoneNumberInput';
import SubmitButton from 'components/SubmitButton';
import CancelButton from 'components/CancelButton';
import Checkbox from 'components/Checkbox';

import PasswordReqs from './PasswordReqs';
import RegistrationSent from './RegistrationSent';
import { CenterBox } from './utils';
import { termsLabel, termsError } from './TermsAndPrivacyCheck';

              
                   
                         
  

function register(values, callbacks) {
    const path = 'registration/customer/';

    callbacks.start();
    post(path, values)
        .then(() => callbacks.done())
        .catch(error => callbacks.fail(error));
}

const DefaultError = () => <Alert flavor='danger'>Correct the errors</Alert>;

const DefaultTitle = () => <h2>Register</h2>;

const AlreadyRegisteredError = ({ error }) => (
    <Alert flavor='info'>
        {error}
        <br />
        <Link to='/'>Login</Link> or <Link to='/reset-password'>Reset Password</Link> to
        use this email address.
        <br />
        Having an issue registering? <Link to='/support'>Submit a support request.</Link>
    </Alert>
);


export class BaseRegister extends Component        {
    state = {
        submitted: false,
    };

    render() {
        const {
            initialValues = {},
            onCancel,
            onSuccess,
            onSubmit = register,
            schema,
            title = <DefaultTitle />
        } = this.props;

        const { submitted } = this.state;

        const successfulRegistration = () => {
            if (onSuccess) {
                onSuccess();
            }
            this.setState({ submitted: true });
        };

        if (submitted) {
            return <RegistrationSent />;
        }

        const jurisdictionOptions = schema.default_jurisdiction_name.choices.map(
            ({ value, display_name: label }) => ({ value, label })
        );

        return (
            <Formik
                initialValues={{
                    first_name: initialValues.first_name || '',
                    last_name: initialValues.last_name || '',
                    email: initialValues.email || '',
                    phone_number: initialValues.phone_number || '',
                    date_of_birth: initialValues.date_of_birth || '',
                    default_jurisdiction_name: initialValues.default_jurisdiction_name || '',
                    password: '',
                    password_rpt: '',
                    agree: false,
                }}
                validate={values => {
                    const errors = {};
                    if (values.password !== values.password_rpt) {
                        errors.password_rpt = 'Passwords do not match.';
                    }
                    if (values.agree !== true) {
                        errors.agree = termsError;
                    }
                    return errors;
                }}
                onSubmit={(values, actions) => {
                    const callbacks = {
                        start: () => {
                            actions.setSubmitting(true);
                            actions.setStatus(null);
                        },
                        done: () => {
                            actions.setSubmitting(false);
                            successfulRegistration();
                        },
                        fail: apiError => {
                            actions.setSubmitting(false);
                            const errors = errorResponseFormatter(apiError.json);
                            const status =
                                errors.email ===
                                'This email address is already registered.' ? (
                                    <AlreadyRegisteredError error={errors.email} />
                                ) : (
                                    <DefaultError />
                                );
                            actions.setStatus(status);
                            actions.setErrors(errors);
                        },
                    };

                    onSubmit(omit(values, ['password_rpt', 'agree']), callbacks);
                }}
                validateOnMount={true}
            >
                {({
                    errors,
                    isValid,
                    isSubmitting,
                    status,
                    values,
                    setFieldValue,
                    touched,
                }) => (
                    <Form className='form' id='register-form'>
                        {status}
                        {title}
                        <Input
                            type='text'
                            name='first_name'
                            label='First Name'
                            autoComplete='given-name'
                            validate={isRequired}
                        />
                        <Input
                            type='text'
                            name='last_name'
                            label='Last Name'
                            autoComplete='family-name'
                            validate={isRequired}
                        />
                        <Input
                            type='text'
                            name='email'
                            label='Email Address'
                            autoComplete='email'
                            validate={chainValidators(isRequired, isEmail)}
                        />
                        <Alert flavor='info'>
                            Please use a personal email address. If you use your work
                            email, and you change employers, you may lose access to your
                            account.
                        </Alert>
                        <FormikDatePicker
                            max={maxBirthDate}
                            min={minBirthDate}
                            name='date_of_birth'
                            label='Date of Birth'
                            validate={isRequired}
                            reverseYears={true}
                        />
                        <FormikSelectInput
                            id='default_jurisdiction_name'
                            name='default_jurisdiction_name'
                            label={schema.default_jurisdiction_name.label}
                            helpText={schema.default_jurisdiction_name.help_text}
                            placeholder='Please select...'
                            options={jurisdictionOptions}
                        />
                        <FormikPhoneNumber
                            name='phone_number'
                            validate={isPhoneNumber}
                            helpText='(Optional)'
                            autoComplete='tel'
                            country={mapJurisdictionToPhoneCountry(
                                values.default_jurisdiction_name
                            )}
                        />
                        <Input
                            type='password'
                            name='password'
                            label='Password'
                            autoComplete='new-password'
                            helpText={
                                <PasswordReqs
                                    className={
                                        status && errors['password']
                                            ? 'text-danger'
                                            : ''
                                    }
                                />
                            }
                            validate={isRequired}
                        />
                        <Input
                            type='password'
                            name='password_rpt'
                            label='Re-Enter Password'
                            autoComplete='new-password'
                            validate={isRequired}
                        />
                        <Checkbox
                            id='agree'
                            checked={values.agree}
                            onChange={() => setFieldValue('agree', !values.agree)}
                            label={termsLabel}
                            error={touched.agree ? errors.agree : null}
                            className='d-block mb-3'
                        />
                        <SubmitButton
                            id='register-submit'
                            disabled={!isValid}
                            submitting={isSubmitting}
                        />

                        <CancelButton onClick={onCancel} />
                    </Form>
                )}
            </Formik>
        );
    }
}

/* wrap Register to fetch schema via options */
export const RegisterForm = props => {
    const optionsUrl = props.optionsUrl || 'registration/customer';

    return (
        <Fetcher
            fetch={({ start, done, fail }) => {
                start();
                options(optionsUrl)
                    .then(resp => done(resp))
                    .catch(e => fail(e));
            }}
            render={({ responseData }) => {
                return <BaseRegister schema={responseData.actions.POST} {...props} />;
            }}
        />
    );
};

const Register = props => (
    <CenterBox>
        <RegisterForm />
    </CenterBox>
);

export default Register;
